/* ==========================================================================
   VOLTIX — theme.js
   Web components natifs, aucune dépendance. Chargé en `defer`.
   Chaque composant est autonome : s'il n'est pas sur la page, rien ne tourne.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Utilitaires partagés ---------- */

  const money = (cents, format) => {
    const value = (cents / 100).toFixed(2).replace('.', ',');
    // Shopify renvoie un gabarit du type "{{amount}} $". On respecte celui de la boutique.
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*\w+\s*\}\}/, value);
    }
    return value + ' $';
  };

  const trapFocus = (container) => {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(selector)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  };

  const lockScroll = (on) => {
    document.documentElement.style.overflow = on ? 'hidden' : '';
  };

  /* ==========================================================================
     ACCORDÉON
     Un seul panneau ouvrable à la fois si data-single est présent.
     ========================================================================== */

  class VoltixAccordion extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('.accordion__trigger'));
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.toggle(trigger));
      });
    }

    toggle(trigger) {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      if (this.hasAttribute('data-single') && !expanded) {
        this.triggers.forEach((other) => { if (other !== trigger) this.setState(other, false); });
      }
      this.setState(trigger, !expanded);
    }

    setState(trigger, open) {
      trigger.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.setAttribute('data-open', String(open));
    }
  }

  /* ==========================================================================
     GALERIE PRODUIT
     Défilement natif + snap. Le JS ne fait que synchroniser points et vignettes.
     ========================================================================== */

  class VoltixGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.gallery__viewport');
      this.slides = Array.from(this.querySelectorAll('.gallery__slide'));
      this.dots = Array.from(this.querySelectorAll('.gallery__dot'));
      this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
      if (!this.viewport || this.slides.length < 2) return;

      // On observe quelle diapo occupe le centre plutôt que d'écouter le scroll.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              this.setActive(this.slides.indexOf(entry.target));
            }
          });
        },
        { root: this.viewport, threshold: [0.6] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));

      [...this.dots, ...this.thumbs].forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.index, 10)));
      });
    }

    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
      this.setActive(index);
    }

    setActive(index) {
      if (index < 0 || index === this.activeIndex) return;
      this.activeIndex = index;
      this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
    }
  }

  /* ==========================================================================
     SÉLECTEUR DE VARIANTE + BUNDLE
     Source de vérité : le JSON des variantes injecté par Liquid.
     ========================================================================== */

  class VoltixProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[name="id"]');
      this.qtyInput = this.querySelector('[name="quantity"]');
      this.submitBtn = this.querySelector('[data-atc]');
      this.priceNow = this.querySelector('[data-price-now]');
      this.priceWas = this.querySelector('[data-price-was]');
      this.priceSave = this.querySelector('[data-price-save]');
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} $';

      const dataEl = this.querySelector('[data-variants]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];

      this.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });
      this.querySelectorAll('[data-bundle-input]').forEach((input) => {
        input.addEventListener('change', () => this.onBundleChange());
      });

      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.onBundleChange();
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-input]:checked')).map((i) => i.value);
    }

    onOptionChange() {
      const chosen = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i])
      );

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.currentVariant = variant;
      if (this.idInput) this.idInput.value = variant.id;

      // On met à jour l'URL sans recharger — le partage du lien garde la variante.
      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.renderPrice(variant);
      this.setAvailability(variant.available);
      this.onBundleChange();

      document.dispatchEvent(new CustomEvent('voltix:variant', { detail: { variant: variant } }));
    }

    renderPrice(variant) {
      if (this.priceNow) this.priceNow.textContent = money(variant.price, this.moneyFormat);
      if (this.priceWas) {
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.priceWas.hidden = !hasCompare;
        if (hasCompare) this.priceWas.textContent = money(variant.compare_at_price, this.moneyFormat);
      }
      if (this.priceSave) {
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.priceSave.hidden = !hasCompare;
        if (hasCompare) {
          const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          this.priceSave.textContent = '−' + pct + ' %';
        }
      }
    }

    setAvailability(available) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = !available;
      const label = available ? this.submitBtn.dataset.labelAvailable : this.submitBtn.dataset.labelSoldOut;
      if (label) this.submitBtn.querySelector('[data-atc-label]').textContent = label;
    }

    setUnavailable() {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = true;
      const label = this.submitBtn.dataset.labelUnavailable;
      if (label) this.submitBtn.querySelector('[data-atc-label]').textContent = label;
    }

    onBundleChange() {
      const selected = this.querySelector('[data-bundle-input]:checked');
      if (!selected) return;
      const qty = parseInt(selected.dataset.quantity, 10) || 1;
      if (this.qtyInput) this.qtyInput.value = qty;

      // Le palier peut porter son propre code de réduction (appliqué au panier).
      this.discountCode = selected.dataset.discount || '';
      document.dispatchEvent(new CustomEvent('voltix:bundle', { detail: { quantity: qty } }));
    }

    async onSubmit(event) {
      // Le drawer n'existe pas forcément ; sans lui on laisse le POST natif se faire.
      const drawer = document.querySelector('voltix-cart-drawer');
      if (!drawer) return;

      event.preventDefault();
      this.submitBtn.dataset.loading = 'true';

      try {
        const body = new FormData(this.form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: body
        });
        if (!response.ok) throw new Error('add');
        await drawer.refresh();
        drawer.open();
      } catch (err) {
        // En cas d'échec on retombe sur la soumission classique plutôt que d'échouer en silence.
        this.form.submit();
      } finally {
        delete this.submitBtn.dataset.loading;
      }
    }
  }

  /* ==========================================================================
     TIROIR PANIER
     ========================================================================== */

  class VoltixCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer');
      this.overlay = this.querySelector('.drawer-overlay');
      this.body = this.querySelector('[data-drawer-body]');

      this.overlay && this.overlay.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      document.querySelectorAll('[data-cart-open]').forEach((btn) =>
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); })
      );

      this.bindLines();
    }

    bindLines() {
      this.querySelectorAll('[data-line-change]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.updateLine(el.dataset.lineKey, parseInt(el.dataset.lineChange, 10));
        });
      });
    }

    open() {
      this.isOpen = true;
      this.panel.setAttribute('data-open', 'true');
      this.overlay.setAttribute('data-open', 'true');
      lockScroll(true);
      this.releaseTrap = trapFocus(this.panel);
      const first = this.panel.querySelector('[data-drawer-close]');
      first && first.focus();
    }

    close() {
      this.isOpen = false;
      this.panel.setAttribute('data-open', 'false');
      this.overlay.setAttribute('data-open', 'false');
      lockScroll(false);
      this.releaseTrap && this.releaseTrap();
    }

    async updateLine(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        if (!response.ok) throw new Error('change');
        await this.refresh();
      } catch (err) {
        window.location.href = '/cart';
      }
    }

    /* On recharge la section rendue par Shopify : le HTML reste piloté par Liquid. */
    async refresh() {
      const sectionId = this.dataset.sectionId;
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      const text = await response.text();
      const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('voltix-cart-drawer');
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;
      this.connectedCallback();
      this.isOpen && this.open();
      this.updateCount();
    }

    async updateCount() {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    }
  }

  /* ==========================================================================
     BARRE D'ACHAT COLLANTE (mobile)
     Apparaît une fois le CTA principal sorti du viewport, se retire au scroll
     vers le haut pour libérer l'écran.
     ========================================================================== */

  class VoltixStickyAtc extends HTMLElement {
    connectedCallback() {
      const targetId = this.dataset.watch;
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;

      document.body.setAttribute('data-sticky-atc', 'true');
      this.ctaPassed = false;
      this.lastY = window.scrollY;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          // `passed` = le CTA est sorti par le haut, pas simplement encore sous le pli.
          this.ctaPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.render();
        },
        { threshold: 0 }
      );
      this.observer.observe(target);

      this.onScroll = () => {
        const y = window.scrollY;
        this.scrollingUp = y < this.lastY - 4;
        this.lastY = y;
        this.render();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // La barre suit la variante et le palier choisis dans le formulaire principal.
      document.addEventListener('voltix:variant', (e) => this.syncVariant(e.detail.variant));
      document.addEventListener('voltix:bundle', (e) => this.syncBundle(e.detail.quantity));

      this.trigger = this.querySelector('[data-sticky-cta]');
      this.trigger && this.trigger.addEventListener('click', () => {
        const btn = target.querySelector('[data-atc]') || target;
        btn.click();
      });

      // Le formulaire produit est plus haut dans le document : il a déjà émis son
      // événement `voltix:bundle` avant qu'on s'y abonne. On lit donc l'état de
      // départ directement plutôt que d'attendre un événement qui est passé.
      const checkedTier = document.querySelector('[data-bundle-input]:checked');
      if (checkedTier) this.quantity = parseInt(checkedTier.dataset.quantity, 10) || 1;
      this.updateSub();
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    render() {
      const visible = this.ctaPassed && !this.scrollingUp;
      this.querySelector('.sticky-atc').setAttribute('data-visible', String(visible));
    }

    syncVariant(variant) {
      const name = this.querySelector('[data-sticky-name]');
      const sub = this.querySelector('[data-sticky-sub]');
      if (name && variant.title && variant.title !== 'Default Title') name.textContent = variant.title;
      if (sub) sub.dataset.price = variant.price;
      this.currentPrice = variant.price;
      this.updateSub();
    }

    syncBundle(quantity) {
      this.quantity = quantity;
      this.updateSub();
    }

    updateSub() {
      const sub = this.querySelector('[data-sticky-sub]');
      if (!sub) return;
      const qty = this.quantity || 1;
      const price = this.currentPrice || parseInt(sub.dataset.basePrice, 10);
      if (!price) return;
      const total = money(price * qty, this.dataset.moneyFormat);
      sub.textContent = qty > 1 ? `${qty} × — ${total}` : total;
    }
  }

  /* ==========================================================================
     POPUP INFOLETTRE
     Jamais immédiat : intention de sortie, ou délai réglé au customizer.
     Refus mémorisé pour ne pas harceler.
     ========================================================================== */

  class VoltixPopup extends HTMLElement {
    connectedCallback() {
      this.key = 'voltix:popup-dismissed';
      if (this.seen()) return;

      this.card = this.querySelector('.popup__card');
      this.querySelectorAll('[data-popup-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close(true))
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close(true);
      });
      this.addEventListener('click', (e) => { if (e.target === this) this.close(true); });

      const delay = parseInt(this.dataset.delay, 10) || 30;
      this.timer = window.setTimeout(() => this.open(), delay * 1000);

      if (this.dataset.exitIntent === 'true') {
        this.onLeave = (e) => { if (e.clientY <= 0) this.open(); };
        document.addEventListener('mouseleave', this.onLeave);
      }
    }

    seen() {
      try { return window.localStorage.getItem(this.key) === '1'; }
      catch (err) { return false; }
    }

    open() {
      if (this.isOpen || this.seen()) return;
      this.isOpen = true;
      this.setAttribute('data-open', 'true');
      this.releaseTrap = trapFocus(this.card);
      const field = this.querySelector('input[type="email"]');
      field && field.focus();
    }

    close(remember) {
      this.isOpen = false;
      this.setAttribute('data-open', 'false');
      this.releaseTrap && this.releaseTrap();
      window.clearTimeout(this.timer);
      this.onLeave && document.removeEventListener('mouseleave', this.onLeave);
      if (remember) {
        try { window.localStorage.setItem(this.key, '1'); } catch (err) { /* navigation privée */ }
      }
    }
  }

  /* ==========================================================================
     COMPTE À REBOURS
     Sur une date réelle. Expiré, il se retire au lieu de repartir en boucle.
     ========================================================================== */

  class VoltixCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = new Date(this.dataset.deadline).getTime();
      if (isNaN(this.deadline)) { this.remove(); return; }
      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() { window.clearInterval(this.interval); }

    tick() {
      const remaining = this.deadline - Date.now();
      if (remaining <= 0) {
        window.clearInterval(this.interval);
        const bar = this.closest('.announcement');
        bar ? bar.remove() : this.remove();
        return;
      }
      const d = Math.floor(remaining / 86400000);
      const h = Math.floor((remaining % 86400000) / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      this.querySelector('[data-cd]').textContent = d > 0
        ? `${d} j ${pad(h)} : ${pad(m)} : ${pad(s)}`
        : `${pad(h)} : ${pad(m)} : ${pad(s)}`;
    }
  }

  /* ==========================================================================
     EN-TÊTE : navigation mobile, recherche
     ========================================================================== */

  class VoltixHeader extends HTMLElement {
    connectedCallback() {
      this.nav = document.getElementById('mobile-nav');
      this.searchPanel = this.querySelector('[data-search-panel]');

      this.querySelectorAll('[data-nav-open]').forEach((btn) =>
        btn.addEventListener('click', () => this.toggleNav(true))
      );
      if (this.nav) {
        this.nav.querySelectorAll('[data-nav-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.toggleNav(false))
        );
      }

      const searchToggle = this.querySelector('[data-search-toggle]');
      searchToggle && searchToggle.addEventListener('click', () => this.toggleSearch());

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.navOpen) this.toggleNav(false);
        if (this.searchOpen) this.toggleSearch();
      });
    }

    toggleNav(open) {
      if (!this.nav) return;
      this.navOpen = open;
      this.nav.setAttribute('data-open', String(open));
      lockScroll(open);
      this.querySelector('[data-nav-open]').setAttribute('aria-expanded', String(open));
      if (open) {
        this.releaseTrap = trapFocus(this.nav);
        const close = this.nav.querySelector('[data-nav-close]');
        close && close.focus();
      } else {
        this.releaseTrap && this.releaseTrap();
        this.querySelector('[data-nav-open]').focus();
      }
    }

    toggleSearch() {
      if (!this.searchPanel) return;
      this.searchOpen = !this.searchOpen;
      this.searchPanel.setAttribute('data-open', String(this.searchOpen));
      this.querySelector('[data-search-toggle]').setAttribute('aria-expanded', String(this.searchOpen));
      if (this.searchOpen) {
        const field = this.searchPanel.querySelector('input[type="search"]');
        field && field.focus();
      }
    }
  }

  /* ==========================================================================
     CHAMP QUANTITÉ
     ========================================================================== */

  class VoltixQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.step, 10);
          const min = parseInt(this.input.min, 10) || 1;
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncDisabled();
        });
      });
      this.syncDisabled();
    }

    syncDisabled() {
      const min = parseInt(this.input.min, 10) || 1;
      const minus = this.querySelector('[data-step="-1"]');
      if (minus) minus.disabled = (parseInt(this.input.value, 10) || min) <= min;
    }
  }

  /* ==========================================================================
     MUR UGC
     Les vidéos jouent en sourdine, en boucle, seulement quand elles sont à
     l'écran — on économise données et batterie plutôt que de tout jouer d'un
     coup. Un tap sur une vidéo active le son. prefers-reduced-motion respecté :
     aucune lecture auto, on montre la pastille de lecture à la place.
     ========================================================================== */

  class VoltixUgc extends HTMLElement {
    connectedCallback() {
      this.videos = Array.from(this.querySelectorAll('.ugc__video'));
      if (!this.videos.length) return;

      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const card = video.closest('.ugc__card');
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              if (!this.reduce) {
                const p = video.play();
                if (p && p.catch) p.catch(function () { /* lecture auto bloquée : sans conséquence */ });
              }
            } else {
              video.pause();
            }
            if (card) card.classList.toggle('is-paused', video.paused);
          });
        },
        { threshold: [0, 0.6] }
      );

      this.videos.forEach((video) => {
        this.observer.observe(video);

        // Tap sur la vidéo : bascule le son et relance si en pause.
        video.addEventListener('click', () => {
          video.muted = !video.muted;
          if (video.paused) {
            const p = video.play();
            if (p && p.catch) p.catch(function () {});
          }
          this.syncCard(video);
        });

        video.addEventListener('play', () => this.syncCard(video));
        video.addEventListener('pause', () => this.syncCard(video));

        // Bouton son dédié (coin haut-droit).
        const soundBtn = video.closest('.ugc__card').querySelector('[data-ugc-mute]');
        if (soundBtn) {
          soundBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            video.muted = !video.muted;
            if (video.paused) {
              const p = video.play();
              if (p && p.catch) p.catch(function () {});
            }
            this.syncCard(video);
          });
        }
      });
    }

    syncCard(video) {
      const card = video.closest('.ugc__card');
      if (card) card.classList.toggle('is-paused', video.paused);
      const soundBtn = card && card.querySelector('[data-ugc-mute]');
      if (soundBtn) soundBtn.dataset.muted = String(video.muted);
    }

    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
  }

  /* ---------- Enregistrement ---------- */

  const registry = {
    'voltix-accordion': VoltixAccordion,
    'voltix-gallery': VoltixGallery,
    'voltix-product-form': VoltixProductForm,
    'voltix-cart-drawer': VoltixCartDrawer,
    'voltix-sticky-atc': VoltixStickyAtc,
    'voltix-popup': VoltixPopup,
    'voltix-countdown': VoltixCountdown,
    'voltix-header': VoltixHeader,
    'voltix-quantity': VoltixQuantity,
    'voltix-ugc': VoltixUgc
  };

  Object.keys(registry).forEach((name) => {
    if (!customElements.get(name)) customElements.define(name, registry[name]);
  });
})();
